import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CheckCircle, Clock, ArrowLeft, CurrencyEur, Warning, DownloadSimple, FileCsv, FilePdf } from "@phosphor-icons/react";

const MONTH_LABELS = {
  "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile",
  "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto",
  "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre",
};

function fmtMonth(m) {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return `${MONTH_LABELS[mm] || mm} ${y}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso.slice(0, 10); }
}

export default function CompensationsOut() {
  const [operators, setOperators] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");

  const loadOps = () => api.get("/compensations/out-operators").then(r => setOperators(r.data));
  useEffect(() => { loadOps(); }, []);

  const loadDetail = (opId) => {
    setSelectedId(opId);
    setSelectedMonth("");
    api.get(`/compensations/operator/${opId}`).then(r => setDetail(r.data));
  };

  const togglePaid = async (comp) => {
    try {
      if (comp.paid) await api.post(`/compensations/${comp._id}/mark-unpaid`);
      else await api.post(`/compensations/${comp._id}/mark-paid`);
      toast.success(comp.paid ? "Segnato come non pagato" : "Segnato come pagato");
      loadDetail(selectedId);
      loadOps();
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  if (selectedId && detail) {
    const allMonths = detail.months.map(m => m.month);
    const currentM = selectedMonth || (allMonths[0] || "");
    const shown = currentM === "__all__" ? detail.months : detail.months.filter(m => m.month === currentM);
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={() => { setSelectedId(null); setDetail(null); setSelectedMonth(""); }} data-testid="back-to-list">
          <ArrowLeft size={14} className="mr-1" /> Torna all'elenco
        </Button>
        <div className="flex items-baseline justify-between mt-4 mb-6 gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-bold">{detail.operator.first_name} {detail.operator.last_name}</h2>
            <p className="text-sm text-muted-foreground">{detail.operator.email} · Compensi impostati per servizio</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Mese</label>
            <select value={currentM} onChange={e => setSelectedMonth(e.target.value)}
              className="h-9 border rounded px-3 text-sm bg-background" data-testid="month-selector-admin">
              <option value="__all__">Tutti i mesi</option>
              {allMonths.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Totali complessivi</div>
            <div className="text-sm mt-1">
              <span className="text-green-700 font-semibold">€{detail.total_paid.toFixed(2)}</span> pagati ·{" "}
              <span className="text-amber-700 font-semibold">€{detail.total_unpaid.toFixed(2)}</span> in attesa
            </div>
          </div>
        </div>

        <ExportBar
          operatorId={selectedId}
          operatorName={`${detail.operator.first_name}_${detail.operator.last_name}`.trim() || "operatore"}
        />

        {shown.length === 0 ? (
          <div className="kpi-card text-center py-12 text-muted-foreground">Nessun servizio completato nel periodo selezionato.</div>
        ) : shown.map(m => (
          <div key={m.month} className="mb-6" data-testid={`admin-month-${m.month}`}>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-display text-lg font-semibold">{fmtMonth(m.month)}</h3>
              <div className="text-xs text-muted-foreground">{m.count} servizi · Totale €{m.total.toFixed(2)}</div>
            </div>
            <div className="kpi-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[color:hsl(var(--surface-alt))]"><tr className="text-left">
                  <th className="py-2 px-4">Data</th>
                  <th>Servizio</th>
                  <th>Indirizzo</th>
                  <th>Professionista</th>
                  <th className="text-right">Compenso</th>
                  <th className="text-center">Pagato</th>
                </tr></thead>
                <tbody>
                  {m.items.map(c => {
                    const addr = [c.property_address, c.property_city].filter(Boolean).join(", ") || "—";
                    return (
                    <tr key={c._id} className="border-t" data-testid={`admin-comp-${c._id}`}>
                      <td className="py-2 px-4">{fmtDate(c.completed_at)}</td>
                      <td className="font-medium">{c.practice_service_name}</td>
                      <td className="text-muted-foreground" data-testid={`admin-comp-address-${c._id}`}>{addr}</td>
                      <td className="text-muted-foreground" data-testid={`admin-comp-professional-${c._id}`}>{c.professional_name || "—"}</td>
                      <td className="text-right font-mono">€{(c.amount || 0).toFixed(2)}</td>
                      <td className="text-center">
                        <PaidToggle comp={c} onConfirm={() => togglePaid(c)} />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <ExportBar operatorId={null} operatorName="tutti" />
      <div className="kpi-card p-0 overflow-hidden">
        {operators.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Nessun operatore OUT configurato. Imposta il tipo "OUT" dalla tab Operatori.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b">
            <th className="py-2 px-4">Operatore</th>
            <th>Email</th>
            <th className="text-center">Ruolo</th>
            <th className="text-center">Servizi</th>
            <th className="text-right">Pagato</th>
            <th className="text-right pr-4">In attesa</th>
          </tr></thead>
          <tbody>
            {operators.map(o => (
              <tr key={o.id} onClick={() => loadDetail(o.id)}
                className="border-b hover:bg-[color:hsl(var(--surface-alt))] cursor-pointer"
                data-testid={`out-op-${o.id}`}>
                <td className="py-3 px-4 font-medium">{o.first_name} {o.last_name}</td>
                <td>{o.email}</td>
                <td className="text-center"><span className="text-xs px-2 py-0.5 bg-[color:hsl(var(--surface-alt))] rounded">{o.role}</span></td>
                <td className="text-center">{o.total_count}</td>
                <td className="text-right text-green-700 font-semibold">€{o.total_paid.toFixed(2)}</td>
                <td className="text-right pr-4 text-amber-700 font-semibold">€{o.total_unpaid.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </div>
    </>
  );
}

function PaidToggle({ comp, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const doAction = async () => {
    await onConfirm();
    setOpen(false);
    setConfirmed(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmed(false); }}>
      <AlertDialogTrigger asChild>
        <button className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${comp.paid ? "text-green-700 bg-green-50 hover:bg-green-100" : "text-amber-700 bg-amber-50 hover:bg-amber-100"}`}
          data-testid={`toggle-paid-${comp._id}`}>
          {comp.paid ? <><CheckCircle size={14} weight="fill" /> Pagato</> : <><Clock size={14} weight="fill" /> In attesa</>}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Warning size={18} weight="fill" color="#B45309" />
            {comp.paid ? "Rimuovere lo stato di pagato?" : "Confermare il pagamento?"}
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2">
            {comp.paid
              ? `Il compenso di €${(comp.amount || 0).toFixed(2)} per "${comp.practice_service_name}" verrà segnato come NON pagato. Confermi?`
              : <>Stai per confermare il pagamento di <strong>€{(comp.amount || 0).toFixed(2)}</strong> per il servizio "{comp.practice_service_name}". <br />Spunta la casella per confermare in modo definitivo.</>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {!comp.paid && (
          <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} data-testid={`double-confirm-${comp._id}`} />
            Confermo di aver pagato l'operatore
          </label>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction
            disabled={!comp.paid && !confirmed}
            onClick={doAction}
            className={comp.paid ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}
            data-testid={`confirm-paid-${comp._id}`}>
            {comp.paid ? "Segna come non pagato" : "Conferma pagamento"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


function ExportBar({ operatorId, operatorName }) {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + "-01";
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(null); // 'csv' | 'pdf' | null

  const doExport = async (format) => {
    setLoading(format);
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (operatorId) params.set("operator_id", operatorId);

      const res = await api.get(`/compensations/export?${params.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: format === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (operatorName || "operatore").replace(/[^a-z0-9_-]+/gi, "_");
      const period = [startDate, endDate].filter(Boolean).join("_") || "tutti";
      a.download = `compensi_${safeName}_${period}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} generato`);
    } catch (e) {
      const msg = e?.response?.status === 403
        ? "Non autorizzato"
        : "Errore durante l'export";
      toast.error(msg);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="kpi-card p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="compensations-export-bar">
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Dal</label>
        <input
          type="date"
          value={startDate}
          max={endDate || undefined}
          onChange={(e) => setStartDate(e.target.value)}
          className="h-9 border rounded px-2 text-sm bg-background"
          data-testid="export-start-date"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Al</label>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(e) => setEndDate(e.target.value)}
          className="h-9 border rounded px-2 text-sm bg-background"
          data-testid="export-end-date"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">&nbsp;</label>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!!loading}
            onClick={() => doExport("csv")}
            data-testid="export-csv-btn"
          >
            <FileCsv size={16} className="mr-1" weight="duotone" />
            {loading === "csv" ? "Genero..." : "Esporta CSV"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!!loading}
            onClick={() => doExport("pdf")}
            data-testid="export-pdf-btn"
          >
            <FilePdf size={16} className="mr-1" weight="duotone" />
            {loading === "pdf" ? "Genero..." : "Esporta PDF"}
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground ml-auto self-center">
        {operatorId ? "Export limitato a questo operatore" : "Export di tutti gli operatori OUT"}
        <br />
        Include colonna "Stato" (Pagato / Da pagare)
      </div>
    </div>
  );
}
